/**
 * WP-06C-B — The immutable pre-cutover Loop recovery snapshot.
 *
 * Founder-approved legacy-data policy, implemented literally:
 *
 *   1. pre-cutover browser Loops are NEVER imported;
 *   2. they are NEVER written to Cloud;
 *   3. they are preserved EXACTLY ONCE in a versioned, read-only snapshot;
 *   4. the snapshot is never surfaced as live product data;
 *   5. no mutation may target a legacy Loop identifier;
 *   6. legacy and hosted Loop lists are never merged;
 *   7. nothing dual-writes;
 *   8. the snapshot is never deleted by this package.
 *
 * "Exactly once" is enforced by refusing to write when a snapshot already
 * exists: the first capture wins forever, so a later app version cannot
 * overwrite the historical record. Once captured, the `loops` array is removed
 * from the live state key, which is what structurally prevents the old browser
 * store from continuing to act as Loop authority.
 *
 * The snapshot is opaque historical evidence. This module deliberately exposes
 * no parser and no product type: a future explicit, user-initiated import or
 * export package owns interpretation.
 */

/** The live prototype state key the pre-cutover build wrote Loops into. */
export const LEGACY_STATE_KEY = "looseend.state.v4";

/** The write-once recovery snapshot key. Versioned so it is never reused. */
export const LEGACY_SNAPSHOT_KEY = "looseend.recovery.loops.v1";

export const LEGACY_SNAPSHOT_VERSION = 1 as const;

/** The live storage format the pre-cutover build wrote. Recorded, never guessed. */
export const LEGACY_SOURCE_FORMAT = "looseend.state" as const;
export const LEGACY_SOURCE_VERSION = 4 as const;

/**
 * A deterministic, non-reversible digest of the preserved rows. FNV-1a over the
 * canonical JSON text: it proves the snapshot was not silently altered, and it
 * carries no readable user content of its own.
 */
export function legacyDigest(loops: readonly unknown[]): string {
  const text = JSON.stringify(loops);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `fnv1a32:${hash.toString(16).padStart(8, "0")}`;
}

export interface LegacyLoopSnapshot {
  readonly version: typeof LEGACY_SNAPSHOT_VERSION;
  readonly capturedAt: string;
  readonly sourceKey: string;
  readonly sourceFormat: typeof LEGACY_SOURCE_FORMAT;
  readonly sourceVersion: typeof LEGACY_SOURCE_VERSION;
  readonly loopCount: number;
  /** Deterministic digest of `loops`; recomputed on read to detect tampering. */
  readonly digest: string;
  /** The raw pre-cutover rows, preserved byte-for-byte as JSON values. */
  readonly loops: readonly unknown[];
}

export type LegacyCaptureOutcome =
  /** A snapshot was written for the first time. */
  | { readonly kind: "captured"; readonly loopCount: number }
  /** A snapshot already exists; the historical record is left untouched. */
  | { readonly kind: "already_captured" }
  /** There were no pre-cutover Loops to preserve. */
  | { readonly kind: "nothing_to_capture" }
  /** Storage is unavailable or unreadable. Nothing was written or removed. */
  | { readonly kind: "unavailable" };

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readJson(store: Storage, key: string): unknown {
  try {
    const raw = store.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

/** True when a snapshot has already been taken. Never overwritten. */
export function hasLegacyLoopSnapshot(): boolean {
  const store = storage();
  if (!store) return false;
  try {
    return store.getItem(LEGACY_SNAPSHOT_KEY) !== null;
  } catch {
    return false;
  }
}

/** Read-only access for a future, explicitly user-initiated recovery package. */
export function readLegacyLoopSnapshot(): LegacyLoopSnapshot | null {
  const store = storage();
  if (!store) return null;
  const value = readJson(store, LEGACY_SNAPSHOT_KEY);
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record["version"] !== LEGACY_SNAPSHOT_VERSION) return null;
  if (!Array.isArray(record["loops"])) return null;
  const loops = record["loops"] as readonly unknown[];
  // An unreadable or altered digest makes the record evidence of nothing, so it
  // is reported as absent rather than surfaced as trustworthy history.
  if (record["digest"] !== legacyDigest(loops)) return null;
  return {
    version: LEGACY_SNAPSHOT_VERSION,
    capturedAt: typeof record["capturedAt"] === "string" ? record["capturedAt"] : "",
    sourceKey: typeof record["sourceKey"] === "string" ? record["sourceKey"] : LEGACY_STATE_KEY,
    sourceFormat: LEGACY_SOURCE_FORMAT,
    sourceVersion: LEGACY_SOURCE_VERSION,
    loopCount: loops.length,
    digest: legacyDigest(loops),
    loops,
  };
}

/**
 * Preserve pre-cutover Loops once, then strip them from the live state key.
 *
 * Called before the local store hydrates, so the browser store can never expose
 * a legacy Loop as current authority — not even for one render.
 */
export function captureLegacyLoopSnapshot(): LegacyCaptureOutcome {
  const store = storage();
  if (!store) return { kind: "unavailable" };

  const live = readJson(store, LEGACY_STATE_KEY);
  const liveRecord =
    live !== null && typeof live === "object" && !Array.isArray(live)
      ? (live as Record<string, unknown>)
      : null;
  const legacyLoops = Array.isArray(liveRecord?.["loops"])
    ? (liveRecord["loops"] as readonly unknown[])
    : [];

  // A snapshot that still reads back intact is HISTORY: it is never overwritten,
  // whatever the live key now contains. A present-but-unreadable one is evidence
  // of nothing, so it may be replaced by a verifiable capture.
  const existing = readLegacyLoopSnapshot();
  const rawPresent = hasLegacyLoopSnapshot();

  const stripLiveLoops = (): boolean => {
    if (!liveRecord || !("loops" in liveRecord)) return true;
    const { loops: _dropped, ...withoutLoops } = liveRecord;
    try {
      store.setItem(LEGACY_STATE_KEY, JSON.stringify(withoutLoops));
      return true;
    } catch {
      return false;
    }
  };

  if (existing !== null) {
    // Preserved already. Retiring the live Loop array is now safe and lossless.
    if (!stripLiveLoops()) return { kind: "unavailable" };
    return { kind: "already_captured" };
  }

  if (legacyLoops.length === 0) {
    if (rawPresent) {
      if (!stripLiveLoops()) return { kind: "unavailable" };
      return { kind: "already_captured" };
    }
    if (!stripLiveLoops()) return { kind: "unavailable" };
    return { kind: "nothing_to_capture" };
  }

  const snapshot: LegacyLoopSnapshot = {
    version: LEGACY_SNAPSHOT_VERSION,
    capturedAt: new Date().toISOString(),
    sourceKey: LEGACY_STATE_KEY,
    sourceFormat: LEGACY_SOURCE_FORMAT,
    sourceVersion: LEGACY_SOURCE_VERSION,
    loopCount: legacyLoops.length,
    digest: legacyDigest(legacyLoops),
    loops: legacyLoops,
  };

  // ORDER IS THE SAFETY PROPERTY: the snapshot is written and read back BEFORE
  // the source rows are removed, so a quota or storage failure leaves the
  // original pre-cutover data completely intact.
  try {
    store.setItem(LEGACY_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    return { kind: "unavailable" };
  }
  if (readLegacyLoopSnapshot() === null) return { kind: "unavailable" };

  if (!stripLiveLoops()) return { kind: "unavailable" };
  return { kind: "captured", loopCount: legacyLoops.length };
}
